import React, {useCallback, useContext} from 'react';

import {WidthItem} from 'src/components/width-item';
import {MessageContext} from 'src/contexts/message-context';

import {FlatButton} from '@pb/kit-component-button';
import {DropZone} from '@pb/kit-component-dropzone';
import {FieldDescription} from '@pb/kit-component-field-description';
import {Group} from '@pb/kit-component-group';
import Icon, {Icons, Sizes} from '@pb/kit-component-icon';
import Label from '@pb/kit-component-label';

import {getFileSize} from './getImageSize';

import {
    imageSelectFooter,
    imageSelectPlaceholder,
    imageSelectPreview,
    imageSelectServiceIcon,
} from './image-select.module.styl';

type Props = {
    width: number,
    height: number,
    maxSize: number,

    label: React.ReactNode,
    description?: React.ReactNode,
    readonly?: boolean,
    hint?: string,

    alt: string,
    img: string | null,
    onRemove: () => void,
    onDrop: (file: File) => void,
    onDownload?: () => void,
};

export const ImageSelect = (props: Props) => {
    const {
        onDrop,
        onRemove,
        onDownload,
        img,
        alt,
        width,
        height,
        label,
        description,
        maxSize,
        readonly,
        hint,
    } = props;
    const {setErrorMessage} = useContext(MessageContext);

    const onDropCallback = useCallback((files: File[]) => {
        const [loadedFile] = files;

        if (getFileSize(loadedFile) > maxSize) {
            setErrorMessage(`File size should be less than ${maxSize} MB`);
            return;
        }

        onDrop(loadedFile);
    }, [maxSize, onDrop, setErrorMessage]);

    const onClear = useCallback(() => {
        onRemove();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <WidthItem width={width}>
            <Group direction="v" gap="xs">
                <WidthItem width="100%">
                    <Group justify="space-between">
                        <Label><b>{label}</b></Label>
                        <Group>
                            {(img?.length && onDownload) ? (
                                <FlatButton
                                    startIcon={<Icon type={Icons.DOWNLOAD} size={Sizes.Size_14}/>}
                                    onClick={onDownload}>
                                    <b>Download</b>
                                </FlatButton>
                            ) : null}
                            {(img?.length && !readonly) ? (
                                <FlatButton
                                    startIcon={<Icon type={Icons.TRASH_LIGHT} size={Sizes.Size_14}/>}
                                    onClick={onClear}>
                                    <b>Remove</b>
                                </FlatButton>
                            ) : null}
                        </Group>
                    </Group>
                </WidthItem>
                {description ? <FieldDescription>{description}</FieldDescription> : null}
                { }
                <div style={{width, height, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                    {readonly && img ? (
                        <img
                            src={img}
                            alt={alt}
                            width={width}
                            height={height}
                            className={imageSelectPreview}
                        />
                    ) : null}
                    {readonly && !img ? <span>No image loaded</span> : null}
                    {!readonly && (
                        <DropZone
                            accept="image/png,image/jpeg"
                            mix=""
                            multiple={false}
                            onDrop={onDropCallback}>
                            {img?.length ? (
                                <img
                                    src={img}
                                    alt={alt}
                                    width={width}
                                    height={height}
                                    className={imageSelectPreview}
                                />
                            ) : (
                                <div className={imageSelectPlaceholder}>
                                    <Icon className={imageSelectServiceIcon} type={Icons.UPLOAD}/>
                                    <span>Drag & drop a file to upload or <a>browse</a></span>
                                    <span className={`disabled font-size font-size_footer ${imageSelectFooter}`}>
                                        Max. file size: {maxSize} MB. Accepted formats: JPG, PNG' {hint}
                                    </span>
                                </div>
                            )}
                        </DropZone>
                    )}
                </div>
            </Group>
        </WidthItem>
    );
};

